/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { useHistory } from 'react-router-dom';

import {
  Button,
  Modal,
  Form,
  Icon,
  Dropzone,
  Segment,
  Checkbox,
} from '@mairistem/composants';

import { TYPES_ACTES_IMPLEMENTES, TYPES_ACTES_OPTIONS } from '@/constants';
import type { TypeActe } from '@/types';
import { useDocumentStore } from '@/zustand';

interface SaisieEvenementModalProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  /**
   * Type d'acte présélectionné à l'ouverture (ex. depuis le « + » d'une
   * fiche, pour repartir du type de l'acte consulté).
   */
  initialTypeActe?: TypeActe;
}

// Seuls les types dont l'assistant est livré : pas de décision ni de PACS en V1.
const TYPES_ACTES_SAISIE_OPTIONS = TYPES_ACTES_OPTIONS.filter(({ key }) =>
  TYPES_ACTES_IMPLEMENTES.includes(key as TypeActe),
);

const SaisieEvenementModal = (props: SaisieEvenementModalProps) => {
  const { open, title, onClose, initialTypeActe } = props;

  const history = useHistory();
  const setDocument = useDocumentStore((state) => state.setDocument);

  const [typeActe, setTypeActe] = React.useState<string>(initialTypeActe ?? '');
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [useIaParsing, setUseIaParsing] = React.useState<boolean>(false);

  // Le composant reste monté entre deux ouvertures (cf. BlocCreation.tsx) :
  // sans cet effet, le type resterait bloqué sur celui de la fois précédente.
  React.useEffect(() => {
    if (open && initialTypeActe) {
      setTypeActe(initialTypeActe);
    }
  }, [open, initialTypeActe]);

  const handleTypeChange = (
    _e: React.SyntheticEvent,
    { value }: { value: string },
  ) => {
    setTypeActe(value);
  };

  const handleValidate = React.useCallback(() => {
    if (!typeActe || !selectedFile) {
      return;
    }
    setDocument(selectedFile);
    onClose();
    history.push(`/assistant?type=${typeActe}`);
  }, [typeActe, selectedFile, setDocument, history, onClose]);

  const handleChangeDrop = React.useCallback((files: File[]) => {
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  }, []);

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header content={title} />
      {/* @ts-expect-error Type error */}
      <Modal.Content style={{ height: '350px' }}>
        <Form.Group>
          <Form.Dropdown
            selection
            options={TYPES_ACTES_SAISIE_OPTIONS}
            value={typeActe}
            label="Type d'acte"
            placeholder="Choisir un type d'acte"
            onChange={handleTypeChange}
            fluid
          />
          <div style={{ marginTop: '20px' }}>
            <Form.Field required>
              <label>Document à intégrer</label>
              <Dropzone
                style={{ cursor: 'pointer' }}
                accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff"
                // @ts-expect-error 2322
                onDrop={(files) => handleChangeDrop(files)}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#f0f0f0',
                    border: '1px dashed #ccc',
                    padding: '15px 20px',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                >
                  <Icon name="FilePlus" iconSet="Lucide" size={24} />
                  <span>Glissez/déposer un fichier ou cliquer</span>
                </div>
              </Dropzone>
              {selectedFile && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px 16px',
                    backgroundColor: '#e8f5e9',
                    border: '1px solid #4caf50',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <Icon
                    name="FileCheck"
                    iconSet="Lucide"
                    size={20}
                    style={{ color: '#4caf50', flexShrink: 0 }}
                  />
                  <span
                    style={{
                      color: '#2e7d32',
                      fontWeight: 500,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#666',
                      flexShrink: 0,
                    }}
                    title="Retirer le fichier"
                  >
                    <Icon name="X" iconSet="Lucide" size={18} />
                  </button>
                </div>
              )}
            </Form.Field>
          </div>
        </Form.Group>
        <Segment
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '20px',
            padding: '16px',
            borderRadius: '8px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon name="Sparkles" iconSet="Lucide" size={22} color="purple" />
            <div>
              <div style={{ fontWeight: 600 }}>
                Utiliser l&apos;IA pour parser le document
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Extraction automatique des informations
              </div>
            </div>
          </div>
          <Checkbox
            toggle
            checked={useIaParsing}
            onChange={() => setUseIaParsing((prev) => !prev)}
          />
        </Segment>
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          size="small"
          icon="close"
          content="Annuler"
          onClick={onClose}
        />
        <Button
          primary
          size="small"
          icon="check"
          content="Valider"
          positive
          disabled={!typeActe || !selectedFile}
          onClick={handleValidate}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default SaisieEvenementModal;
